/**
 * Flow UI (VX-1) foundational runtime.
 *
 * The token system is additive: the CSS is always present, but the semantic
 * remap only activates once `data-flow-ui="on"` is painted onto <html>. That
 * only happens when the `flow_ui_enabled` feature flag resolves true, so the
 * flag is the single switch that governs the visual transformation.
 */

export const FLOW_UI_FLAG_KEY = "flow_ui_enabled";
export const FLOW_UI_ATTRIBUTE = "data-flow-ui";

/** Canonical freight states. Order is the operational lifecycle order. */
export const FREIGHT_STATES = ["planned", "moving", "proven", "attention", "blocked"] as const;
export type FreightState = (typeof FREIGHT_STATES)[number];

/** Semantic token bound to each freight state (VX-1 hue contract). */
export const FREIGHT_STATE_TOKENS: Record<FreightState, string> = {
  planned: "--move-indigo",
  moving: "--flow-cyan",
  proven: "--proven-green",
  attention: "--attention-amber",
  blocked: "--blocked-red",
};

/** Single motion vocabulary (ms). Anything outside this scale is a defect. */
export const MOTION_SCALE = {
  instant: 90,
  quick: 160,
  settle: 260,
  travel: 420,
  ambient: 2400,
} as const;
export type MotionStep = keyof typeof MOTION_SCALE;

export const MOTION_EASINGS = {
  flow: "cubic-bezier(0.22, 1, 0.36, 1)",
  enter: "cubic-bezier(0.16, 1, 0.3, 1)",
  exit: "cubic-bezier(0.4, 0, 1, 1)",
} as const;

/** True when the OS asks us to stand still. Motion is decoration, never proof. */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Duration for a motion step, collapsed to 0 under reduced motion. */
export function motionDuration(step: MotionStep, reduced = prefersReducedMotion()): number {
  return reduced ? 0 : MOTION_SCALE[step];
}

/** Paint or clear the Flow UI attribute. Pure DOM, no flag logic. */
export function applyFlowUi(on: boolean, root?: HTMLElement | null): void {
  const el = root ?? (typeof document !== "undefined" ? document.documentElement : null);
  if (!el) return;
  if (on) el.setAttribute(FLOW_UI_ATTRIBUTE, "on");
  else el.removeAttribute(FLOW_UI_ATTRIBUTE);
}

export function isFlowUiActive(root?: HTMLElement | null): boolean {
  const el = root ?? (typeof document !== "undefined" ? document.documentElement : null);
  return el?.getAttribute(FLOW_UI_ATTRIBUTE) === "on";
}
